import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import Layout from "@/components/layout/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissions } from "@/hooks/use-permissions";
import { useQuery } from "@tanstack/react-query";
import KPICards from "@/components/dashboard/kpi-cards";
import ClaimsTable from "@/components/claims/claims-table";
import QuickActions from "@/components/dashboard/quick-actions";
import StatusDistribution from "@/components/dashboard/status-distribution";
import ActivityTrackingWidget from "@/components/dashboard/activity-tracking-widget";
import RepairFollowupWidget from "@/components/dashboard/repair-followup-widget";
import AdjusterDashboardContent from "@/components/dashboard/adjuster-dashboard-content";
import GmailPreviewWidget from "@/components/dashboard/gmail-preview-widget";
import GmailConnectModal from "@/components/dashboard/gmail-connect-modal";
import { FirstClaimPanel, useClaimCount } from "@/components/onboarding/first-claim-panel";
import { GmailReminderBanner, useGmailReminder } from "@/components/onboarding/gmail-reminder-banner";
import { useIsNewBetaFirmUser } from "@/hooks/use-new-beta-firm";

export default function Dashboard() {
  const {
    canViewDashboardOverview,
    canViewDashboardAdjuster,
    canViewClaims,
    canViewTasks,
    canViewAssignedCommunications,
    isLoading,
  } = usePermissions();
  const [, setLocation] = useLocation();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const isNewBetaFirmUser = useIsNewBetaFirmUser();
  const claimCount = useClaimCount(canViewClaims && isNewBetaFirmUser);

  const { data: gmailAccounts = [], isLoading: accountsLoading } = useQuery<{ id: string; isActive: boolean }[]>({
    queryKey: ['/api/gmail/accounts'],
    enabled: !isLoading && canViewAssignedCommunications,
  });

  // An owner who skipped Gmail in onboarding (#287) gets a reminder instead of the dialog on every visit.
  const { skippedGmail, reminderLoading } = useGmailReminder(isNewBetaFirmUser && canViewAssignedCommunications);
  // The first-claim panel (#286) already carries the connect prompt while a new beta firm has no claims.
  const panelPromptsGmail = isNewBetaFirmUser && canViewClaims && claimCount.count === 0;

  // Auto-open connect modal on every dashboard visit when Gmail is not connected or all accounts are inactive/revoked.
  // A new beta firm with no claims gets the same prompt inside the first-claim panel, so the modal would only cover it.
  useEffect(() => {
    if (!isLoading && !accountsLoading && canViewAssignedCommunications && !claimCount.isLoading && !reminderLoading && !skippedGmail && !panelPromptsGmail) {
      const hasActiveAccount = gmailAccounts.some((a) => a.isActive);
      if (!hasActiveAccount) {
        setShowConnectModal(true);
      }
    }
  }, [isLoading, accountsLoading, canViewAssignedCommunications, gmailAccounts, claimCount.isLoading, reminderLoading, skippedGmail, panelPromptsGmail]);

  // Determine default tab based on available permissions
  const defaultTab = canViewDashboardOverview ? "overview" : canViewDashboardAdjuster ? "adjuster" : "overview";
  const hasSomeDashboardAccess = canViewDashboardOverview || canViewDashboardAdjuster;

  // Auto-redirect users without dashboard access to the first permitted page
  useEffect(() => {
    if (isLoading || hasSomeDashboardAccess) return;

    if (canViewClaims) {
      setLocation("/claims");
    } else if (canViewTasks) {
      setLocation("/tasks");
    } else {
      // Chat is always accessible
      setLocation("/chat");
    }
  }, [isLoading, hasSomeDashboardAccess, canViewClaims, canViewTasks, setLocation]);

  if (isLoading || !hasSomeDashboardAccess) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  // If user only has one tab available, show it without the tab bar
  const showTabs = canViewDashboardOverview && canViewDashboardAdjuster;

  const gmailCard = canViewAssignedCommunications ? (
    <GmailPreviewWidget onConnectClick={() => setShowConnectModal(true)} />
  ) : null;

  return (
    <Layout
      breadcrumbs={[
        { label: "Dashboard", href: "/" },
        { label: defaultTab === "overview" ? "Overview" : "Adjuster", current: true },
      ]}
    >
      {skippedGmail && !panelPromptsGmail && !gmailAccounts.some((a) => a.isActive) && (
        <div className="mb-6">
          <GmailReminderBanner onConnect={() => setShowConnectModal(true)} />
        </div>
      )}
      <div className="mb-6 empty:hidden">
        <FirstClaimPanel onConnectGmail={() => setShowConnectModal(true)} />
      </div>
      {showTabs ? (
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="adjuster">Adjuster</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <p className="text-muted-foreground mb-4">Company-wide KPIs and analytics</p>
            <OverviewContent gmailCard={gmailCard} />
          </TabsContent>

          <TabsContent value="adjuster">
            <p className="text-muted-foreground mb-4">Your personalized claims overview</p>
            <AdjusterDashboardContent gmailCard={gmailCard} />
          </TabsContent>
        </Tabs>
      ) : canViewDashboardOverview ? (
        <>
          <p className="text-muted-foreground mb-4">Company-wide KPIs and analytics</p>
          <OverviewContent gmailCard={gmailCard} />
        </>
      ) : (
        <>
          <p className="text-muted-foreground mb-4">Your personalized claims overview</p>
          <AdjusterDashboardContent gmailCard={gmailCard} />
        </>
      )}
      {canViewAssignedCommunications && (
        <GmailConnectModal
          open={showConnectModal}
          onOpenChange={setShowConnectModal}
          isReconnect={gmailAccounts.length > 0 && !gmailAccounts.some((a) => a.isActive)}
        />
      )}
    </Layout>
  );
}

function OverviewContent({ gmailCard }: { gmailCard: React.ReactNode }) {
  return (
    <>
      <KPICards />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
        <div className="lg:col-span-2">
          <ClaimsTable />
        </div>

        <div className="space-y-4 sm:space-y-6">
          <ActivityTrackingWidget />
          <RepairFollowupWidget />
          <QuickActions />
          <StatusDistribution />
          {gmailCard}
        </div>
      </div>
    </>
  );
}
